import styles from './APIUsageTable.module.scss';
import { useAPIUsages } from 'src/hooks';
import { useCallback, useState } from 'react';
import { SpinnerSimple } from 'src/v2/commonComponents/SpinnerSimple';
import { API_USAGES_COLUMNS } from 'src/constants';
import dayjs from 'dayjs';
import {
  getPriceFromNanos,
  getPriceToCents,
  getPriceWithCurrencyWithoutRounding,
} from 'src/utils';
import { Button } from 'src/v2/commonComponents/Button';
import { CreditAccountTransaction } from 'src/types/wallet/models/CreditAccountTransaction';
import { env } from 'src/env';

export const APIUsageTable = () => {
  const [page, setPage] = useState<{ nextPage: string; prevPage: string }>({
    nextPage: '',
    prevPage: '',
  });
  const {
    usagesData,
    isLoading,
    nextPageToken,
    previousPageToken,
    isFetching,
  } = useAPIUsages({
    nextKey: page.nextPage,
    prevKey: page.prevPage,
  });

  const handleNextPage = () => {
    setPage({
      nextPage: nextPageToken,
      prevPage: '',
    });
  };

  const handlePrevClick = () => {
    setPage({
      nextPage: '',
      prevPage: previousPageToken,
    });
  };
  const taskURL = (conversation_id: string): string =>
    `${env.REACT_APP_SUPER_NINJA_URL}/agents/${conversation_id}`;

  const getTaskLink = useCallback((usage: CreditAccountTransaction) => {
    if (usage.conversation_id) {
      return taskURL(usage.conversation_id);
    }
    return null;
  }, []);

  const renderTaskLink = useCallback(
    (usage: CreditAccountTransaction) => {
      const link = getTaskLink(usage);
      if (!link) {
        return usage.consumption_model_name ?? `-`;
      }
      return (
        <Button
          as="a"
          href={link}
          rel="noopener noreferrer"
          className={styles.taskLink}
        >
          View Task
        </Button>
      );
    },
    [getTaskLink],
  );
  const renderPrice = (usage: CreditAccountTransaction) => {
    const priceNanos = getPriceFromNanos(usage.amount_nanos);
    if (priceNanos > 0 && priceNanos < 0.01) {
      return getPriceWithCurrencyWithoutRounding(priceNanos, usage.currency);
    }
    return getPriceToCents(priceNanos, usage.currency);
  };

  return (
    <div className={styles.root}>
      {usagesData?.length === 0 && !isLoading && (
        <span className={styles.noDataText}>
          You haven’t made any requests yet. As soon as you do, they’ll be
          listed here with all the details.
        </span>
      )}
      {isLoading && (
        <div className={styles.emptyState}>
          <SpinnerSimple />
        </div>
      )}
      {isFetching && !isLoading && (
        <div className={styles.loadingOverlay}>
          <SpinnerSimple />
        </div>
      )}
      {usagesData.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.row}>
              {API_USAGES_COLUMNS.map((col) => (
                <th key={col.key} className={styles.headCell}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(usagesData || []).map((usage, rowIndex) => {
              return (
                <tr key={rowIndex} className={styles.row}>
                  {/* <td className={styles.cell}>
                    {usage.consumption_input_token_count}
                  </td>
                  <td className={styles.cell}>
                    {usage.consumption_output_token_count}
                  </td>
                  <td className={styles.cell}>
                    {getPriceWithCurrencyWithoutRounding(
                      getPriceFromNanos(usage.consumption_net_input_cost_nanos),
                      usage.currency,
                    )}
                  </td>
                  <td className={styles.cell}>
                    {getPriceWithCurrencyWithoutRounding(
                      getPriceFromNanos(
                        usage.consumption_net_output_cost_nanos,
                      ),
                      usage.currency,
                    )}
                  </td> */}
                  <td className={styles.cell}>
                    {dayjs(usage.created_at).format('MMM D, YYYY')}
                  </td>
                  <td className={styles.cell}>{renderPrice(usage)}</td>
                  <td className={styles.cell}>
                    <span>{renderTaskLink(usage)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!isLoading && usagesData?.length !== 0 && (
        <div className={styles.paginationRow}>
          <Button
            color="transparent"
            disabled={!previousPageToken || isFetching}
            onClick={handlePrevClick}
          >
            Previous
          </Button>
          <Button
            color="transparent"
            disabled={!nextPageToken || isFetching}
            onClick={handleNextPage}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
